import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  first,
  map,
  Observable,
  take,
} from 'rxjs';
import { FileModel } from '../models/file.model';
import { LinkModel } from '../models/link.model';
import { TrainingCategoryModel } from '../models/training-category.model';
import { FileService } from './file.service';
import { LinkService } from './link.service';

@Injectable({
  providedIn: 'root',
})
export class FileManagerStateService {
  private filesSubject = new BehaviorSubject<FileModel[]>([]);
  private linksSubject = new BehaviorSubject<LinkModel[]>([]);

  private initialFiles: FileModel[] = [];
  private initialLinks: LinkModel[] = [];

  private activeSearchTerm: string = '';
  private activeTagFilterIds: string[] = [];
  private activeCategoryFilter: TrainingCategoryModel | null = null;

  constructor(
    private fileService: FileService,
    private linkService: LinkService
  ) {}

  fetchFilesBySeminarId(seminarId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.fileService
        .getFilesBySeminar(seminarId)
        .pipe(take(1))
        .subscribe({
          next: (files: FileModel[]) => {
            this.filesSubject.next(files);
            this.initialFiles = files;
            resolve();
          },
          error: () => {
            reject();
          },
        });
    });
  }

  fetchLinksBySeminarId(seminarId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.linkService
        .getLinksBySeminar(seminarId)
        .pipe(take(1))
        .subscribe({
          next: (links: LinkModel[]) => {
            this.linksSubject.next(links);
            this.initialLinks = links;
            resolve();
          },
          error: () => {
            reject();
          },
        });
    });
  }

  fetchFilesByUser(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.fileService
        .getFilesByUser()
        .pipe(take(1))
        .subscribe({
          next: (files: FileModel[]) => {
            this.filesSubject.next(files);
            this.initialFiles = files;
            resolve();
          },
          error: () => {
            reject();
          },
        });
    });
  }

  fetchFilesBySeminars(seminarIds: number[]): Promise<void> {
    return new Promise((resolve, reject) => {
      this.fileService
        .getFilesBySeminars(seminarIds)
        .pipe(take(1))
        .subscribe({
          next: (files: FileModel[]) => {
            this.filesSubject.next(files);
            this.initialFiles = files;
            resolve();
          },
          error: () => {
            reject();
          },
        });
    });
  }

  fetchAllFilesAdmin(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.fileService
        .getAllFilesAdmin()
        .pipe(take(1))
        .subscribe({
          next: (files: FileModel[]) => {
            this.filesSubject.next(files);
            this.initialFiles = files;
            resolve();
          },
          error: () => {
            reject();
          },
        });
    });
  }

  fetchLinksByUser(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.linkService
        .getLinksByUser()
        .pipe(take(1))
        .subscribe({
          next: (links: LinkModel[]) => {
            this.linksSubject.next(links);
            this.initialLinks = links;
            resolve();
          },
          error: () => {
            reject();
          },
        });
    });
  }

  fetchLinksBySeminars(seminarIds: number[]): Promise<void> {
    return new Promise((resolve, reject) => {
      this.linkService
        .getLinksBySeminars(seminarIds)
        .pipe(take(1))
        .subscribe({
          next: (links: LinkModel[]) => {
            this.linksSubject.next(links);
            this.initialLinks = links;
            resolve();
          },
          error: () => {
            reject();
          },
        });
    });
  }

  fetchAllLinksAdmin(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.linkService
        .getAllLinksAdmin()
        .pipe(take(1))
        .subscribe({
          next: (links: LinkModel[]) => {
            this.linksSubject.next(links);
            this.initialLinks = links;
            resolve();
          },
          error: () => {
            reject();
          },
        });
    });
  }

  addFile(file: FileModel): void {
    const currentFiles = this.filesSubject.getValue();
    this.filesSubject.next([...currentFiles, file]);
  }

  addLink(link: LinkModel): void {
    const currentLinks = this.linksSubject.getValue();
    this.linksSubject.next([...currentLinks, link]);
  }

  removeFile(fileId: number): void {
    const updatedFiles = this.filesSubject
      .getValue()
      .filter(file => file.id !== fileId);
    this.filesSubject.next(updatedFiles);
  }

  removeLink(linkId: number): void {
    const updatedLinks = this.linksSubject
      .getValue()
      .filter(link => link.id !== linkId);
    this.linksSubject.next(updatedLinks);
  }

  getFiles(): FileModel[] {
    return this.filesSubject.getValue();
  }

  getLinks(): LinkModel[] {
    return this.linksSubject.getValue();
  }

  isFileAndLinkEmpty(): boolean {
    return (
      this.filesSubject.getValue().length === 0 &&
      this.linksSubject.getValue().length === 0
    );
  }

  hasActiveFilters(): boolean {
    return !!(this.activeTagFilterIds.length || this.activeSearchTerm);
  }

  getFilesLinksCountByCategory(categoryId: number): Observable<number> {
    return combineLatest([this.filesSubject, this.linksSubject]).pipe(
      first(),
      map(() => {
        const countItemsByCategory = (
          items: { category1?: { id: number }; category2?: { id: number } }[]
        ) =>
          items.filter(
            item =>
              item.category1?.id === categoryId ||
              item.category2?.id === categoryId
          ).length;

        return (
          countItemsByCategory(this.initialFiles) +
          countItemsByCategory(this.initialLinks)
        );
      })
    );
  }

  getAllFilesLinksCount(): Observable<number> {
    return combineLatest([this.filesSubject, this.linksSubject]).pipe(
      first(),
      map(() => {
        return this.initialFiles.length + this.initialLinks.length;
      })
    );
  }

  applySearchFilter(searchTerm: string): void {
    this.activeSearchTerm = searchTerm.trim();
    this.applyAllFilters();
  }

  applyTagFilter(activeTagFilterIds: (string | number)[] | null | undefined): void {
    this.activeTagFilterIds = (activeTagFilterIds ?? []).map(id => String(id));
    this.applyAllFilters();
  }

  applyCategoryFilter(selectedCategory: TrainingCategoryModel | null): void {
    this.activeCategoryFilter = selectedCategory;
    this.applyAllFilters();
  }

  private applyAllFilters(): void {
    let filteredFiles = [...this.initialFiles];
    let filteredLinks = [...this.initialLinks];

    // Apply category filter
    if (this.activeCategoryFilter) {
      filteredFiles = filteredFiles.filter(
        file =>
          file.category1?.id === this.activeCategoryFilter?.id ||
          file.category2?.id === this.activeCategoryFilter?.id
      );
      filteredLinks = filteredLinks.filter(
        link =>
          link.category1?.id === this.activeCategoryFilter?.id ||
          link.category2?.id === this.activeCategoryFilter?.id
      );
    }

    // Apply tag filter
    if (this.activeTagFilterIds.length > 0) {
      const activeTagIds = new Set(this.activeTagFilterIds);
      filteredFiles = filteredFiles.filter(file =>
        file.tags?.some(tag => activeTagIds.has(String(tag.id)))
      );
      filteredLinks = filteredLinks.filter(link =>
        link.tags?.some(tag => activeTagIds.has(String(tag.id)))
      );
    }

    // Apply search filter
    if (this.activeSearchTerm) {
      filteredFiles = filteredFiles.filter(file =>
        file.name.toLowerCase().includes(this.activeSearchTerm.toLowerCase())
      );
      filteredLinks = filteredLinks.filter(link =>
        link.title.toLowerCase().includes(this.activeSearchTerm.toLowerCase())
      );
    }

    // Update subjects
    this.filesSubject.next(filteredFiles);
    this.linksSubject.next(filteredLinks);
  }

  public setFiles(files: FileModel[]): void {
    this.filesSubject.next(files);
  }

  public setLinks(links: LinkModel[]): void {
    this.linksSubject.next(links);
  }

  clear(): void {
    this.filesSubject.next([]);
    this.linksSubject.next([]);
    this.initialFiles = [];
    this.initialLinks = [];
    this.activeSearchTerm = '';
    this.activeTagFilterIds = [];
    this.activeCategoryFilter = null;
  }
}
